import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

const TONES = {
  success: ["paid", "success", "processed", "sent", "active", "full", "completed", "approved", "operational", "delivered", "accepted", "won", "done", "posted", "received", "resolved", "reconciled", "matched", "confirmed", "closed_won", "qualified", "converted", "ready"],
  warning: ["part_paid", "pending", "retrying", "past_due", "read_only", "pending_approval", "low_stock", "under_maintenance", "due_soon", "on_hold", "partially_received", "partially_paid", "partial", "on_leave", "qa"],
  danger: ["unpaid", "failed", "dead", "overdue", "blocked", "critical", "rejected", "lost", "suspended", "breakdown", "expired", "void", "voided", "cancelled", "closed_lost", "error"],
  info: ["trialing", "comped", "in_progress", "scheduled", "draft", "created", "open", "issued", "submitted", "ordered", "trial", "new", "contacted", "proposal", "negotiation", "prepress", "in_production", "finishing"],
};
const TONE_CLS = { success: "po-tone-success", warning: "po-tone-warning", danger: "po-tone-danger", info: "po-tone-info", neutral: "po-tone-neutral" };
const STRIPE_CLS = { success: "po-stripe-success", warning: "po-stripe-warning", danger: "po-stripe-danger", info: "po-stripe-info", brand: "po-stripe-brand" };
const TONE_OF = Object.fromEntries(Object.entries(TONES).flatMap(([t, list]) => list.map((s) => [s, t])));
export const toneOf = (status) => TONE_OF[status] || "neutral";

export function StatusBadge({ status, testId, className, label: override, tone }) {
  const label = override || (status || "—").replace(/_/g, "-");
  return (
    <span data-testid={testId} data-tone={tone || toneOf(status)} className={cn("po-chip", TONE_CLS[tone || toneOf(status)], className)}>
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, children, badge }) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between fade-up">
      <div className="min-w-0 flex-1 lg:min-w-[22rem]">
        <h1 className="break-words text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}{badge && <span className="ml-3 inline-block align-middle">{badge}</span>}</h1>
        {subtitle && <p className="mt-1 break-words text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2 lg:justify-end">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, testId, accent, tone }) {
  const stripe = tone || (accent ? "brand" : null);
  return (
    <div className={cn("po-card min-w-0 p-5 fade-up hover:[box-shadow:var(--po-shadow-raise)]", stripe && "po-stripe", STRIPE_CLS[stripe])} data-testid={testId} data-tone={stripe || undefined}>
      <div className="po-label">{label}</div>
      <div className="mt-2 break-words text-xl font-semibold tabular-nums tracking-tight text-slate-900 lg:text-2xl">{value}</div>
      {hint && <div className="mt-1 text-xs leading-relaxed text-slate-500">{hint}</div>}
    </div>
  );
}

export function Section({ title, action, children, className, testId }) {
  return (
    <section className={cn("po-card fade-up", className)} data-testid={testId}>
      {(title || action) && (
        <div className="po-card-header">
          <h3 className="po-card-title">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function NativeSelect({ value, onChange, options, testId, className, placeholder, disabled }) {
  return (
    <select data-testid={testId} disabled={disabled} className={cn("po-select", disabled && "cursor-not-allowed opacity-70", className)} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o, i) => (
        <option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function StackTable({ children, className, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = ref.current;
    const apply = () => {
      const heads = [...t.querySelectorAll("thead th")].map((th) => th.textContent.trim());
      t.querySelectorAll("tbody tr").forEach((tr) => [...tr.children].forEach((td, i) => { const l = td.colSpan > 1 ? "" : heads[i] || ""; if (td.getAttribute("data-label") !== l) td.setAttribute("data-label", l); }));
    };
    apply();
    const mo = new MutationObserver(apply);
    mo.observe(t, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);
  return <table ref={ref} className={cn("po-table po-stack", className)} {...rest}>{children}</table>;
}

export function EmptyRow({ cols, text = "Nothing here yet." }) {
  return (
    <tr>
      <td colSpan={cols} className="!py-10 text-center text-slate-400">{text}</td>
    </tr>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
